import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Button,
  Card,
  Icon,
  PieChart,
  PieChartDataPoint,
} from '@node-space/ui-components'
import { Portfolio } from 'types/rewards'
import { useRewardsSheetContext } from '../context/RewardsSheetProvider'
import { useRewardsSummary } from '../hooks/useRewardsSummary'
import { RewardsActionIds } from '../types'
import { formatAmount } from '../utils'

export interface RewardsSummaryProps {
  userPortfolios: Portfolio[]
}

export const RewardsSummary = ({ userPortfolios }: RewardsSummaryProps) => {
  const { t } = useTranslation()
  const { handleSheetOpen } = useRewardsSheetContext()

  const { portfoliosSummary, totalBalance, totalEarnings, totalPendingEarnings } =
    useRewardsSummary(userPortfolios)

  const handleRemoveAllocation = () => {
    handleSheetOpen({
      actionId: RewardsActionIds.REMOVE_ALLOCATION,
      header: t('rewards.removeAllocation.title'),
    })
  }

  return (
    <Box height="full" flex>
      <Card variant="static" emphasis full>
        <Box
          align="center"
          height="full"
          justify="center"
          gap={24}
          className="text-center lg:text-left  flex-col lg:flex lg:flex-row"
        >
          <AllocationSummary
            totalBalance={totalBalance}
            totalEarnings={totalEarnings}
            // As we have only USDC portfolio, showing USD as user currency
            userCurrency="USD"
            pendingEarnings={totalPendingEarnings}
            onRemoveAllocation={handleRemoveAllocation}
          />

          {portfoliosSummary.length > 1 && <AllocationChart data={portfoliosSummary} />}
        </Box>
      </Card>
    </Box>
  )
}

const PendingEarnings = ({ onRemoveAllocation }: { onRemoveAllocation: () => void }) => {
  const { t } = useTranslation()

  return (
    <Box className="ml-[46px]">
      <Button variant="ghost" noPadding onClick={onRemoveAllocation} className="underline">
        {t('rewards.removeAllocation.removeAllocation')}
      </Button>
    </Box>
  )
}

const AllocationChart = ({ data }: { data: PieChartDataPoint[] }) => {
  return (
    <Box flex justify="center" align="center" className="md:items-start">
      <Box className="w-[220px] h-[220px] text-foreground-prominent">
        <PieChart
          dataKey="value"
          data={data}
          Icon={<Icon size="large" name="SketchLogo" />}
          innerRadius="55%"
        />
      </Box>
    </Box>
  )
}

const AllocationSummary = ({
  totalBalance,
  totalEarnings,
  userCurrency,
  pendingEarnings,
  onRemoveAllocation,
}: {
  totalBalance: number
  totalEarnings: number
  userCurrency: string
  pendingEarnings: number
  onRemoveAllocation: () => void
}) => {
  const { t } = useTranslation()

  return (
    <Box flexCol justify="center" gapY={8} paddingY={8} className="md:min-w-[282px] flex-1">
      <p className="text-body-regular-small text-foreground-prominent">
        {t('rewards.totalAllocatedBalanceWithPending')}
      </p>

      <Box flex align="center" justify="center" gap={8} className="lg:justify-start">
        <Avatar name={userCurrency} type="currency" />
        <h1 className="text-h1 text-foreground-prominent">{formatAmount(totalBalance)}</h1>
      </Box>

      {pendingEarnings > 0 && <PendingEarnings onRemoveAllocation={onRemoveAllocation} />}

      <Box
        flex
        align="center"
        justify="center"
        gap={8}
        className="lg:justify-start text-foreground-prominent"
      >
        <Icon name="SketchLogo" size="medium" />
        <p className="text-strong-small text-foreground-subdued">
          {t('rewards.allTimeEarnings', {
            currency: userCurrency,
            amount: formatAmount(totalEarnings),
          })}
        </p>
      </Box>
    </Box>
  )
}
